import { JobCard } from "@/components/jobs/job-card";
import { JobSearchBar, type JobSearchFilters } from "@/components/jobs/job-search-bar";
import { PageHero } from "@/components/page-hero";
import { PageLayout } from "@/components/page-layout";
import { useAuthModal } from "@/context/auth-modal";
import { client } from "@/utils/orpc";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@super-resume-new/ui/components/pagination";
import { Spinner } from "@super-resume-new/ui/components/spinner";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Briefcase, TrendingUp, Verified, X } from "lucide-react";
import { toast } from "sonner";
import { canonicalUrl, jsonLdScript, seo } from "@/utils/seo";
import { breadcrumbSchema } from "@/utils/schema";

export interface JobsSearchSchema {
  q?: string;
  skill?: string;
  country?: string;
  workMode?: "remote" | "hybrid" | "onsite";
  seniority?: "intern" | "junior" | "mid" | "senior" | "staff" | "principal" | "manager";
  hasSalary?: boolean;
  postedWithinDays?: number;
  saved?: boolean;
  page?: number;
}

export const Route = createFileRoute("/jobs/")({
  validateSearch: (search: Record<string, unknown>): JobsSearchSchema => ({
    q: typeof search.q === "string" && search.q.trim() ? search.q.trim() : undefined,
    skill: typeof search.skill === "string" && search.skill.trim() ? search.skill.trim() : undefined,
    country:
      typeof search.country === "string" && search.country.trim()
        ? search.country.trim().toUpperCase()
        : undefined,
    workMode:
      search.workMode === "remote" || search.workMode === "hybrid" || search.workMode === "onsite"
        ? search.workMode
        : undefined,
    seniority:
      typeof search.seniority === "string" &&
      ["intern", "junior", "mid", "senior", "staff", "principal", "manager"].includes(
        search.seniority,
      )
        ? (search.seniority as any)
        : undefined,
    hasSalary: search.hasSalary === true || search.hasSalary === "true" ? true : undefined,
    postedWithinDays:
      typeof search.postedWithinDays === "number"
        ? search.postedWithinDays
        : typeof search.postedWithinDays === "string" && !isNaN(Number(search.postedWithinDays))
          ? Number(search.postedWithinDays)
          : undefined,
    saved: search.saved === true || search.saved === "true" ? true : undefined,
    page:
      typeof search.page === "number" && search.page > 0
        ? search.page
        : typeof search.page === "string" && Number(search.page) > 0
          ? Number(search.page)
          : undefined,
  }),
  loaderDeps: ({ search }) => search,
  // Server-render page one. Without this the directory ships an empty <ul> to a
  // crawler and every job page is reachable only through the sitemap.
  loader: async ({ deps }) => {
    try {
      return await client.jobs.list({
        query: deps.q || undefined,
        skill: deps.skill || undefined,
        country: deps.country || undefined,
        workMode: deps.workMode || undefined,
        seniority: deps.seniority || undefined,
        hasSalary: deps.hasSalary || undefined,
        postedWithinDays: deps.postedWithinDays || undefined,
        saved: deps.saved || undefined,
        page: deps.page ?? 1,
        limit: 10,
      });
    } catch {
      return null;
    }
  },
  head: ({ match, loaderData }) => {
    const search = match.search as JobsSearchSchema;
    const title = jobsDirectoryTitle(search);
    const head = seo({
      title,
      description: jobsDirectoryDescription(search),
      path: jobsDirectoryPath(search),
      // A keyword search or a personal "saved" view is a search-results page.
      // Google asks for those to stay out of the index; the facet views stay in.
      noindex: Boolean(search.q || search.saved),
    });

    const items = loaderData?.items ?? [];

    return {
      ...head,
      scripts: [
        jsonLdScript(
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tech Jobs", path: "/jobs" },
          ]),
        ),
        ...(items.length
          ? [
              jsonLdScript({
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: title,
                numberOfItems: items.length,
                itemListElement: items.map((job: { slug: string; title: string }, index: number) => ({
                  "@type": "ListItem",
                  position: index + 1,
                  name: job.title,
                  url: canonicalUrl(`/jobs/${job.slug}`),
                })),
              }),
            ]
          : []),
      ],
    };
  },
  component: JobsDirectoryComponent,
});


const WORK_MODE_LABEL: Record<string, string> = {
  remote: "Remote",
  hybrid: "Hybrid",
  onsite: "On-site",
};

const SENIORITY_LABEL: Record<string, string> = {
  intern: "Internship",
  junior: "Junior",
  mid: "Mid-level",
  senior: "Senior",
  staff: "Staff",
  principal: "Principal",
  manager: "Engineering Manager",
};

/** A facet view is a real landing page, so it gets its own title rather than
 *  reusing the directory's and competing with it. */
function jobsDirectoryTitle(search: JobsSearchSchema) {
  const parts = [
    search.skill ? `${search.skill}` : "",
    search.seniority ? SENIORITY_LABEL[search.seniority] : "",
    search.workMode ? WORK_MODE_LABEL[search.workMode] : "",
  ].filter(Boolean);
  const scope = parts.length ? `${parts.join(" ")} Tech Jobs` : "Tech Jobs Directory";
  const page = search.page && search.page > 1 ? ` — Page ${search.page}` : "";
  return `${scope} with Free ATS Match Check${page} | JobCues`;
}

function jobsDirectoryDescription(search: JobsSearchSchema) {
  const parts = [
    search.skill ? `requiring ${search.skill}` : "",
    search.seniority ? SENIORITY_LABEL[search.seniority].toLowerCase() : "",
    search.workMode ? WORK_MODE_LABEL[search.workMode].toLowerCase() : "",
  ].filter(Boolean);
  const scope = parts.length ? `${parts.join(" ")} ` : "";
  return `Browse verified ${scope}software engineering openings pulled straight from company applicant tracking systems. Upload a resume once and get an instant match score, matched keywords, and missing keywords for any listing.`;
}

/** Canonical path. Only the facets worth indexing survive into it, so the same
 *  listing set cannot be indexed under a dozen parameter permutations. */
function jobsDirectoryPath(search: JobsSearchSchema) {
  const params = new URLSearchParams();
  if (search.skill) params.set("skill", search.skill);
  if (search.workMode) params.set("workMode", search.workMode);
  if (search.seniority) params.set("seniority", search.seniority);
  if (search.country) params.set("country", search.country);
  if (search.page && search.page > 1) params.set("page", String(search.page));
  const qs = params.toString();
  return qs ? `/jobs?${qs}` : "/jobs";
}

function getPaginationRange(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, "ellipsis", totalPages];
  }
  if (currentPage >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages];
}

function JobsDirectoryComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { openAuthModal } = useAuthModal();

  const page = search.page ?? 1;

  const filters: JobSearchFilters = {
    query: search.q ?? "",
    country: search.country ?? "",
    workMode: search.workMode ?? "",
    seniority: search.seniority ?? "",
    hasSalary: Boolean(search.hasSalary),
    postedWithinDays: search.postedWithinDays ?? "",
    saved: Boolean(search.saved),
  };

  // Saved job IDs for the active user
  const { data: savedIds = [] } = useQuery({
    queryKey: ["jobs", "savedIds"],
    queryFn: async () => client.jobs.getSavedIds(),
  });

  const savedSet = new Set(savedIds);

  const initialData = Route.useLoaderData();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["jobs", "list", search],
    initialData: initialData ?? undefined,
    // The route loader already fetched this exact search. Without a staleTime
    // the query refetches it immediately, so every filter change costs two
    // requests. Keep the old list on screen while a new one loads.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return client.jobs.list({
        query: search.q || undefined,
        skill: search.skill || undefined,
        country: search.country || undefined,
        workMode: search.workMode || undefined,
        seniority: search.seniority || undefined,
        hasSalary: search.hasSalary || undefined,
        postedWithinDays: search.postedWithinDays || undefined,
        saved: search.saved || undefined,
        page,
        limit: 10,
      });
    },
  });

  const toggleSaveMutation = useMutation({
    mutationFn: async (jobId: string) => client.jobs.toggleSave({ jobId }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["jobs", "savedIds"] });
      if (search.saved) {
        queryClient.invalidateQueries({ queryKey: ["jobs", "list"] });
      }
      toast.success(
        res.saved ? "Job saved to your bookmarks!" : "Job removed from saved bookmarks.",
      );
    },
    onError: () => {
      openAuthModal({
        title: "Save Jobs",
        description: "Sign in to save jobs to your bookmarks and review them anytime.",
      });
    },
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;
  // The server stops counting at COUNT_CAP rather than scan the whole table for
  // a number nobody reads, so past that the total is a floor: "1,000+".
  const countLabel = `${totalCount.toLocaleString()}${data?.countCapped ? "+" : ""}`;

  const handleFilterChange = (newFilters: JobSearchFilters) => {
    navigate({
      search: (prev) => ({
        ...prev,
        q: newFilters.query || undefined,
        country: newFilters.country || undefined,
        workMode: newFilters.workMode || undefined,
        seniority: newFilters.seniority || undefined,
        hasSalary: newFilters.hasSalary ? true : undefined,
        postedWithinDays: newFilters.postedWithinDays
          ? Number(newFilters.postedWithinDays)
          : undefined,
        saved: newFilters.saved ? true : undefined,
        page: undefined, // Reset to page 1 on filter change
      }),
      replace: true,
    });
  };

  const handlePageChange = (newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage === 1 ? undefined : newPage,
      }),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <PageLayout id="jobs-directory-content" className="max-w-7xl">
      <PageHero
        eyebrow="Verified openings"
        icon={Verified}
        title={
          <>
            <strong>Find your next role</strong>
            <br />
            tailored to your stack
          </>
        }
        description="Engineering and tech roles from company boards, with the fit criteria pulled out."
      />

      {/* Hiring Trends Banner */}
      <div className="mb-6 flex justify-center">
        <Link
          to="/jobs/tech-hiring-trends"
          className="group inline-flex items-center gap-2 rounded-full border border-violet-500/20 bg-violet-500/5 px-4 py-1.5 text-xs font-medium text-violet-700 dark:text-violet-300 transition-all hover:border-violet-500/40 hover:bg-violet-500/10 shadow-xs"
        >
          <TrendingUp className="size-3.5 text-violet-500" />
          <span>Explore 2026 Tech Hiring Trends & In-Demand Skills</span>
          <ArrowRight className="size-3 text-violet-500 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="mb-8">
        <JobSearchBar filters={filters} onChange={handleFilterChange} />
        {search.skill ? (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Active skill filter:</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300 shadow-xs">
              <span>{search.skill}</span>
              <button
                type="button"
                onClick={() =>
                  navigate({
                    search: (prev) => ({ ...prev, skill: undefined, page: undefined }),
                    replace: true,
                  })
                }
                className="rounded-full p-0.5 hover:bg-violet-500/20 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                title="Clear skill filter"
                aria-label="Clear skill filter"
              >
                <X className="size-3" />
              </button>
            </span>
          </div>
        ) : null}
      </div>

      {/* Listings List (1 Column / 1 Row per job) */}
      {isLoading ? (
        <div className="flex min-h-[360px] flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center shadow-sm">
          <Spinner className="mb-4 size-6 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Loading job opportunities</h3>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Fetching latest verified postings and requirements…
          </p>
        </div>
      ) : isError ? (
        <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-border bg-card p-8 text-center shadow-sm">
          <h3 className="text-sm font-semibold text-foreground">Failed to load jobs</h3>
          <p className="mt-1 text-[13px] text-muted-foreground">
            There was an issue connecting to the database. Please try again.
          </p>
          <Button onClick={() => refetch()} variant="outline" size="sm" className="mt-4">
            Retry
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-xl border border-dashed border-border p-8 text-center">
          <Briefcase className="mb-3 size-5 text-muted-foreground" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-foreground">No matching jobs found</h3>
          <p className="mt-1 max-w-sm text-[13px] leading-relaxed text-muted-foreground">
            We couldn't find active jobs matching your search and filter criteria. Try clearing some
            filters or adjusting your search keyword.
          </p>
          <Button
            onClick={() => {
              handleFilterChange({
                query: "",
                country: "",
                workMode: "",
                seniority: "",
                hasSalary: false,
                postedWithinDays: "",
                saved: false,
              });
            }}
            variant="outline"
            size="sm"
            className="mt-4"
          >
            Clear all filters
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3.5">
          {items.map((j) => (
            <JobCard
              key={j.id}
              id={j.id}
              slug={j.slug}
              title={j.title}
              company={j.company}
              seniority={j.seniority}
              yearsMin={j.yearsMin}
              yearsMax={j.yearsMax}
              workMode={j.workMode}
              locationPrimary={j.locationPrimary}
              country={j.country}
              compMin={j.compMin}
              compMax={j.compMax}
              compCurrency={j.compCurrency}
              compPeriod={j.compPeriod}
              postedAt={j.postedAt}
              oneLiner={j.oneLiner}
              topSkills={j.topSkills}
              isSaved={savedSet.has(j.id)}
              onToggleSave={() => toggleSaveMutation.mutate(j.id)}
            />
          ))}

          {/* Coss UI Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border pt-6">
              <p className="text-xs text-muted-foreground">
                Showing <span className="font-medium text-foreground">{(page - 1) * 10 + 1}</span>–
                <span className="font-medium text-foreground">
                  {Math.min(page * 10, totalCount)}
                </span>{" "}
                of <span className="font-medium text-foreground">{countLabel}</span> jobs
              </p>

              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => {
                        if (page > 1) {
                          handlePageChange(page - 1);
                        }
                      }}
                      className={
                        page <= 1
                          ? "pointer-events-none opacity-40 cursor-not-allowed"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>

                  {getPaginationRange(page, totalPages).map((p, idx) =>
                    p === "ellipsis" ? (
                      <PaginationItem key={`ellipsis-${idx}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={p}>
                        <PaginationLink
                          isActive={page === p}
                          onClick={() => {
                            handlePageChange(p as number);
                          }}
                          className="cursor-pointer"
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => {
                        if (page < totalPages) {
                          handlePageChange(page + 1);
                        }
                      }}
                      className={
                        page >= totalPages
                          ? "pointer-events-none opacity-40 cursor-not-allowed"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
}
